<template>
  <div class="bulk-task-job-package-editor container py-3">
        <!-- Main Editor Content -->
        <div v-if="!currentPackage.params.goal">
          <!-- Goal Selection -->
          <h1 class="mb-4">Create Bulk Task Job Package</h1>
          <p class="lead">Select the type of bulk task you want to create.</p>

          <div class="mb-4 d-flex justify-content-end">
            <button
              type="button"
              class="btn btn-outline-primary"
              @click="showBulkDefinitionsList = true"
            >
              <i class="bi bi-folder2-open me-1"></i>
              Open Existing Package
            </button>
          </div>

          <div class="row g-4">
            <!-- Timeline Export -->
            <div class="col-md-4">
              <div class="card h-100 shadow-sm goal-card" @click="selectGoal('timeline')" role="button">
                <div class="card-body">
                  <h5 class="card-title">
                    <i class="bi bi-calendar-range me-2"></i>Timeline Export
                  </h5>
                  <p class="card-text">
                    Export historical weather data for specific locations and date ranges.
                  </p>
                </div>
              </div>
            </div>

            <!-- Statistical Summary -->
            <div class="col-md-4">
              <div class="card h-100 shadow-sm goal-card" @click="selectGoal('statisticalsummary')" role="button">
                <div class="card-body">
                  <h5 class="card-title">
                    <i class="bi bi-graph-up me-2"></i>Statistical Summary
                  </h5>
                  <p class="card-text">
                    Calculate aggregated statistics like maximum temperature or total precipitation.
                  </p>
                </div>
              </div>
            </div>

            <!-- Frost Dates -->
            <div class="col-md-4">
              <div class="card h-100 shadow-sm goal-card" @click="selectGoal('frostdates')" role="button">
                <div class="card-body">
                  <h5 class="card-title">
                    <i class="bi bi-thermometer-snow me-2"></i>Frost Dates
                  </h5>
                  <p class="card-text">
                    Identify first and last frost dates based on temperature thresholds.
                  </p>
                </div>
              </div>
            </div>

            <!-- Region Stats -->
            <div class="col-md-4">
              <div class="card h-100 shadow-sm goal-card position-relative" @click="selectGoal('regionstats')" role="button">
                <span class="position-absolute top-0 end-0 badge bg-warning text-dark px-3 py-2">
                  Coming Soon
                </span>
                <div class="card-body opacity-40">
                  <h5 class="card-title">
                    <i class="bi bi-map me-2"></i>Region Statistics
                  </h5>
                  <p class="card-text">
                    Analyze weather patterns across geographic regions.
                  </p>
                </div>
              </div>
            </div>

             <!-- Route Weather -->
            <div class="col-md-4">
              <div class="card h-100 shadow-sm goal-card position-relative" @click="selectGoal('routeweather')" role="button">
                <span class="position-absolute top-0 end-0 badge bg-warning text-dark px-3 py-2">
                  Coming Soon
                </span>
                <div class="card-body opacity-40">
                  <h5 class="card-title">
                    <i class="bi bi-map me-2"></i>Route Weather
                  </h5>
                  <p class="card-text">
                    Analyze weather patterns along specific routes.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Property Editors based on Goal -->
        <div v-else class="mt-4">
            <div class="card mb-4 border-primary">
                <div class="card-body d-flex align-items-center justify-content-between">
                    <div>
                        <h4 class="mb-1 text-primary">
                        <i class="bi bi-flag"></i>
                        {{ goalDisplayName }}
                        </h4>
                        <small class="text-muted">Configure your <strong>{{ goalDisplayName }}</strong> bulk task below.</small>
                    </div>
                    <button type="button" class="btn btn-sm btn-outline-primary" @click="clearGoal">
                        <i class="bi bi-chevron-left"></i> Change Goal
                    </button>
                </div>
            </div>

          <!-- Package Name and Description -->
          <div class="card mb-4">
            <div class="card-header">
              <h5 class="mb-0">Package Information</h5>
            </div>
            <div class="card-body">
              <div class="mb-3">
                <label for="packageName" class="form-label">Name *</label>
                <input 
                  v-model="metadata.name" 
                  type="text" 
                  class="form-control" 
                  id="packageName"
                  :placeholder="defaultName"
                  required
                >
              </div>
              <div class="mb-3">
                <label for="packageDesc" class="form-label">Description</label>
                <textarea 
                  v-model="metadata.desc" 
                  class="form-control" 
                  id="packageDesc"
                  rows="2"
                ></textarea>
              </div>
            </div>
          </div>

          <!-- Locations Editor -->
          <div class="card mb-4">
            <div class="card-header">
              <h5 class="mb-0">Locations</h5>
            </div>
            <div class="card-body">
              <LocationsEditor
                :parentLocations="currentPackage.locations || []"
                :sessionState="sessionState"
                @update:locations="updateLocations"
              />
            </div>
          </div>

          <!-- Date Range (for all goals) -->
          <div class="card mb-4">
            <div class="card-header">
              <h5 class="mb-0">Date Range</h5>
            </div>
            <div class="card-body">
                <DateRangeChooser class="" :queryDefinition="currentPackage.params" @change:daterange="setDateRange" ></DateRangeChooser>
            </div>
          </div>

           <!-- Data options for all goals -->
          <div class="card mb-4">
            <div class="card-header">
              <h5 class="mb-0">Data Options</h5>
            </div>
            <div class="card-body">

                

                <UnitGroupDropdown v-model="queryDefn.unitGroup" />
                
               <button 
                type="button" 
                class="btn btn-sm btn-outline-primary"
                @click="showTimelineQueryEditor = true"
              >
                <i class="bi bi-bell me-1"></i>
                Configure data options
              </button>
            </div>
          </div>
          <!-- ContentTypeChooser (for all goals) -->
          <div class="card mb-4">
            <div class="card-header">
              <h5 class="mb-0">Content Type</h5>
            </div>
            <div class="card-body">
                <ContentTypeChooser :queryDefinition="queryDefn" />
            </div>
          </div>

          <div class="card mb-4">
            <div class="card-header d-flex justify-content-between align-items-center">
                <h5 class="mb-0">Raw data information</h5>
               <button  type="button" class="btn btn-sm btn-outline-primary" @click="refreshPreviewRawData">
                    <i class="bi bi-arrow-repeat"></i> Refresh
                </button>
            </div>
            <div class="card-body">
                <div v-if="previewRawData.isRefreshing" class="d-flex align-items-center">
                    <div class="spinner-border mx-2 me-3" role="status" aria-hidden="true"></div>
                    <strong>Refreshing...</strong>
                </div>
                <div v-else-if="previewRawData.errorMessage">
                    <strong>Error:</strong> {{ previewRawData.errorMessage }}
                </div>
                <div v-else-if="previewRawData.resultData" class="text-success">
                   <div class="row mb-2">
                        <div class="col-md-6">
                            <strong>Estimated cost per location:</strong>
                            <span class="ms-2">{{ previewRawData.resultData.queryCost }}</span>
                        </div>
                         <div class="col-md-6">
                            <strong>Estimated total cost:</strong>
                            <span class="ms-2">{{ previewRawData.resultData.queryCost * locationCount }}</span>
                        </div>
                    </div>
                </div>
                <div v-else>
                    Refresh to view the raw data preview information.
                </div>
            </div>
          </div>
          <!-- Stats Definition Editor (for statistical goals) -->
          <div v-if="isStatisticalGoal" class="card mb-4">
            <div class="card-header">
              <h5 class="mb-0">Statistics Configuration</h5>
            </div>
            <div class="card-body">
              <component
                :is="currentStatsEditor"
                :definition="statsDefinition"
                @update="updateStatsDefinition"
              />
            </div>
          </div>

          <!-- Filters Editor -->
          <div class="card mb-4">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h5 class="mb-0">Filters</h5>
                <button  type="button" class="btn btn-sm btn-outline-primary" @click="openResultFilterEditor">
                    <i class="bi bi-funnel"></i> Configure
                </button>
            </div>
            <div class="card-body">
              <div v-if="!hasFilters" class="text-muted">
                No filters configured
              </div>
              <div v-else>
                <div v-for="(c, idx) in currentPackage.resultFilter.criteria" :key="idx" class="mb-2">
                  <span class="text-muted">{{ c.object }}</span>
                  <span class="text-muted">{{ c.element }}</span>
                  <span class="text-muted">{{ c.operator }}</span>
                  <span class="text-muted">{{ c.value }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Notifications Editor -->
          <div class="card mb-4">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h5 class="mb-0">Notifications</h5>
              <button 
                type="button" 
                class="btn btn-sm btn-outline-primary"
                @click="showNotificationEditor = true"
              >
                <i class="bi bi-bell me-1"></i>
                Configure
              </button>
            </div>
            <div class="card-body">
              <div v-if="!hasNotifications" class="text-muted">
                No notifications configured
              </div>
              <div v-else>
                <div v-for="config in currentPackage.notificationConfigs" :key="config.notificationType" class="mb-2">
                  <span class="badge bg-primary me-2">{{ getNotificationTypeLabel(config.notificationType) }}</span>
                  <span class="text-muted">{{ config.recipients.length }} recipient(s)</span>
                </div>
              </div>
            </div>
          </div>



                     

          <!-- Schedule Configuration -->
          <div class="card mb-4">
            <div class="card-header">
              <h5 class="mb-0">Execution Options &amp; Schedule</h5>
            </div>
            <div class="card-body">

                <div class="form-check mb-3">
                    <input 
                    v-model="currentPackage.enabled" 
                    class="form-check-input" 
                    type="checkbox" 
                    id="enabled"
                    >
                    <label class="form-check-label" for="enabled">
                    Enabled
                    </label>
                </div>

              <div class="form-check mb-3">
                <input 
                  v-model="currentPackage.runOnce" 
                  class="form-check-input" 
                  type="checkbox" 
                  id="runOnce"
                >
                <label class="form-check-label" for="runOnce">
                  Run once
                </label>
              </div>
              
                <div class="mb-3">
                    <h5>Schedule</h5>
                    <div class="form-label">Run Times (UTC)</div>
                    <div v-for="(time, idx) in currentPackage.runTimes" :key="idx" class="input-group mb-2">
                        <select
                            class="form-select"
                            style="max-width:80px;"
                            :value="getHour(time)"
                            @change="setRunTimeHour(idx, $event.target.value)"
                        >
                            <option v-for="hour in 24" :key="hour" :value="hour-1">
                            {{ String(hour-1).padStart(2, '0') }}
                            </option>
                        </select>
                        <span class="input-group-text">:</span>
                        <select
                            class="form-select"
                            style="max-width:80px;"
                            :value="getMinute(time)"
                            @change="setRunTimeMinute(idx, $event.target.value)"
                        >
                            <option v-for="min in [0, 15, 30, 45]" :key="min" :value="min">
                            {{ String(min).padStart(2, '0') }}
                            </option>
                        </select>
                        <button type="button" class="btn btn-outline-danger" @click="removeRunTime(idx)">
                            <i class="bi bi-trash"></i>
                        </button>
                    </div>
                    <button type="button" class="btn btn-outline-primary btn-sm" @click="addRunTime">
                    <i class="bi bi-plus-circle"></i> Add Time
                    </button>
                    <!--<div>{{currentPackage.runTimes }}</div>-->
                </div>
            </div>
          </div>


            <!-- Schedule Configuration -->
          <div class="card mb-4">
            <div class="card-header">
              <h5 class="mb-0">Results &amp; Preview</h5>
            </div>
            <div class="card-body">
                <div v-if="pollingState.isRefreshingResults" class="d-flex align-items-center">
                    <div class="spinner-border mx-2 me-3" role="status" aria-hidden="true"></div>
                    <strong>{{processingStatusText}}</strong>
                </div>
                <div v-else-if="hasResultError">
                    <strong>Error:</strong> {{ taskInstance.errorMessage }}
                </div>
                <div v-else-if="hasResult" class="text-success">
                   <div class="row mb-2">
                        <div class="col-md-6">
                        <strong>Completed Jobs:</strong>
                        <span class="ms-2">{{ taskInstance.completed }} / {{ taskInstance.total }}</span>
                        </div>
                        <div class="col-md-6">
                        <strong>Queued Jobs:</strong>
                        <span class="ms-2">{{ taskInstance.queuedJobs }}</span>
                        </div>
                    </div>
                    <div class="row mb-2">
                        <div class="col-md-6">
                        <strong>Successful:</strong>
                        <span class="ms-2 text-success">{{ taskInstance.successful }}</span>
                        </div>
                        <div class="col-md-6">
                        <strong>Failed:</strong>
                        <span class="ms-2 text-danger">{{ taskInstance.failed }}</span>
                        </div>
                    </div>
                    <div class="row mb-2">
                        <div class="col-md-6">
                        <strong>Content Type:</strong>
                        <span class="ms-2">{{ taskInstance.contentType }}</span>
                        </div>
                        <div class="col-md-6">
                        <strong>Info:</strong>
                        <span class="ms-2">{{ taskInstance.info }}</span>
                        </div>
                    </div>
                    <div class="row mb-2">
                        <div class="col-md-6">
                        <strong>Completed At:</strong>
                        <span class="ms-2">{{ taskInstance.endDatetime }}</span>
                        </div>
                        <div class="col-md-6">
                        <strong>Expires:</strong>
                        <span class="ms-2">{{ taskInstance.expirationDateTime }}</span>
                        </div>
                    </div>
                </div>
            </div>
          </div>


          <!-- Action Buttons -->
          <div class="d-flex justify-content-end gap-2 mt-4">

            <button type="button" class="btn btn-secondary" @click="clearGoal">
              <i class="bi bi-arrow-left me-1"></i>Back
            </button>
            <button 
              type="button" 
              class="btn btn-primary" 
              @click="savePackage"
              :disabled="!isValid || isSaving"
            >
              <span v-if="isSaving" class="spinner-border spinner-border-sm me-2" role="status"></span>
              <i v-else class="bi bi-save me-1"></i>
              {{ metadata.id ? 'Update' : 'Create' }} Package
            </button>
            <!--
            <button 
              v-if="currentPackage.runOnce"
              type="button" 
              class="btn btn-success" 
              @click="saveAndRun"
              :disabled="!isValid || isSaving"
            >
              <span v-if="isSaving" class="spinner-border spinner-border-sm me-2" role="status"></span>
              <i v-else class="bi bi-play-fill me-1"></i>
              Save & Run Now
            </button>
            -->
          </div>
        </div>

    <!-- Timeline Query Editor Modal -->
    <TimelineQueryEditor
      v-if="showTimelineQueryEditor"
      :show="showTimelineQueryEditor"
      :queryDefinition="currentPackage.params || {}"
      @save="updateQueryDefn"
      @cancel="showTimelineQueryEditor = false"
    />

    <!-- Notification Editor Modal -->
    <NotificationEditor 
      v-if="showNotificationEditor"
      :show="showNotificationEditor"
      :notificationConfigs="currentPackage.notificationConfigs || []"
      @save="updateNotifications"
      @cancel="showNotificationEditor = false"
      @openAddressBook="openAddressBook"
    />

    <!-- Address Book Editor Modal -->
    <template v-if="showAddressBook">
      <div class="modal-backdrop fade show"></div>
      <div  class="modal fade show d-block" tabindex="-1">
        <div class="modal-dialog modal-lg modal-dialog-centered">
          <div class="modal-content">
            <AddressBookEditor ref="addressBookEditor" />
          </div>
        </div>
      </div>
    </template>

    <!-- Result Filter Editor Modal -->
    <ResultFilterEditor
        v-if="showResultFilterEditor"
        :resultFilter="currentPackage.resultFilter || {}"
        @save="updateResultFilter"
        @cancel="showResultFilterEditor = false"
        />
 

    <template v-if="showBulkDefinitionsList">


      <div  class="modal-backdrop fade show"></div>
      <div class="modal fade show d-block" tabindex="-1" >

        <div class="modal-dialog modal-lg modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">
                Bulk definitions
              </h5>
              <button type="button" class="btn-close" @click="showBulkDefinitionsList=false"></button>
            </div>
            <div class="modal-body">
              <BulkDefinitionsList :sessionId="sessionId" :sessionState="sessionState"  @select:task="setMetadata"></BulkDefinitionsList>
            </div>
          </div>
        </div>
      </div>
    </template>



  </div>
</template>

<script>

import LocationsEditor from './LocationsEditor.vue';
import StatisticalSummaryEditor from './weatherstatistics/StatisticalSummaryEditor.vue';
import FrostDatesEditor from './weatherstatistics/FrostDatesEditor.vue';
import NotificationEditor from './NotificationEditor.vue';
import AddressBookEditor from './AddressBookEditor.vue';
import ResultFilterEditor from './ResultFilterEditor.vue';
import DateRangeChooser from './DateRangeChooser.vue';
import TimelineQueryEditor from './modalwrappers/TimelineQueryEditor.vue';
import ContentTypeChooser from './ContentTypeChooser.vue';
import BulkDefinitionsList from './BulkDefinitionsList.vue';
import {VcJobStatus} from  '/src/visualcrossing-core/vcjobstatus';
import {VcListUtils} from '/src/visualcrossing-core/vclistutils';
import UnitGroupDropdown from './UnitGroupDropdown.vue';

export default {
  name: 'BulkTaskJobPackageEditor',
  components: {
    UnitGroupDropdown,
    BulkDefinitionsList,
    LocationsEditor,
    StatisticalSummaryEditor,
    FrostDatesEditor,
    NotificationEditor,
    AddressBookEditor,
    ResultFilterEditor,
    DateRangeChooser,
    TimelineQueryEditor,
    ContentTypeChooser
  },
  props: {
    sessionId: String, // <-- Add this line
    sessionState: Object, // <-- And this line
    packageId: String // Optional: for editing existing package
  },
  emits: ['opensignup', 'saved', 'executed'],
  data() {
    return {
       
        metadata: {
            id: null,
            name: '',
            desc: '',
            type: 'JobPackage'
        },
        currentPackage: {
            params:{
                "goal":null,
                "startdate":null,
                "enddate":null,
                "unitGroup":"metric",
                "contentType":"json",
                "options":"",
                "include":"",
                "maxDistance":-1,
            },
            locations: [],
            notificationConfigs: [],
            runOnce: true,
            enabled: true,
            runTimes: [],
            type:4,
            accountUserId: null
        },
        showTimelineQueryEditor: false,
        showNotificationEditor: false,
        showAddressBook: false,
        showResultFilterEditor: false,
        showBulkDefinitionsList: false,
        isSaving: false,
        isLoading: false,
        taskInstance: null,
        pollingState: {
            isPolling: false,
            pollingRetries: 0,
            hasExceedRetries: false,
            isRefreshingResults: false,
            pollingTimer: null,
        },
        previewRawData: {
            errorMessage: null,
            isRefreshing: false,
            resultData: null
        }
    };
  },
  computed: {
    locationCount() {
      return this.currentPackage.locations ? this.currentPackage.locations.length : 0;
    },
    processingStatusText() {
      if (!this.taskInstance) return "Submitting...";
      if (this.taskInstance.status===1) return "Queued";
      if (this.taskInstance.status===2) {
        if (this.taskInstance.completed>1 && this.taskInstance.total>1) {
          return `Running ${this.taskInstance.completed} of ${this.taskInstance.total}`;
        } else {
          return "Running";
        }
      }
      return null;
    },
    hasResult() {
      return this.taskInstance && this.taskInstance.status===3;
    },
    hasResultError() {
      return this.taskInstance && this.taskInstance.status===4;
    },
    goal() {
      return this.currentPackage.params.goal;
    },
    vcCommunicator() {
      return window.vcCommunicator || null;
    },
    goalDisplayName() {
      const goals = {
        'timeline': 'Timeline Export',
        'statisticalsummary': 'Statistical Summary',
        'frostdates': 'Frost Dates',
        'regionstats': 'Region Statistics'
      };
      return goals[this.currentPackage.params.goal] || this.currentPackage.params.goal;
    },
    queryDefn() {
      return this.currentPackage.params || {};
    },
    isStatisticalGoal() {
      return ['statisticalsummary', 'frostdates', 'regionstats'].includes(this.currentPackage.params.goal);
    },
    currentStatsEditor() {
      switch (this.currentPackage.params.goal) {
        case 'timeline': return 'Export';
        case 'statisticalsummary': return 'StatisticalSummaryEditor';
        case 'frostdates': return 'FrostDatesEditor';
        default: return null;
      }
    },
    statsDefinition() {
      try {
        return this.currentPackage.params.statsdefinition 
          ? JSON.parse(this.currentPackage.params.statsdefinition) 
          : {};
      } catch (e) {
        return {};
      }
    },
    defaultName() {
      if (!this.currentPackage.locations || this.currentPackage.locations.length === 0) {
        return `${this.goalDisplayName} Task`;
      }
      const firstLocation = this.currentPackage.locations[0].name || this.currentPackage.locations[0].location;
      return `${this.goalDisplayName} - ${firstLocation}`;
    },
    hasNotifications() {
      return this.currentPackage.notificationConfigs && this.currentPackage.notificationConfigs.length > 0;
    },
    hasFilters() {
      return this.currentPackage.resultFilter && this.currentPackage.resultFilter.criteria.length > 0;
    },
    isValid() {
      return this.metadata.name && 
             this.currentPackage.locations.length > 0;
    },
    

  },
  mounted() {
    if (this.packageId) {
      this.loadPackage();
    }
  },
  methods: {
    setMetadata(metadata, packageDefn) {
        console.log("setmetadata...")
        Object.assign(this.metadata, metadata);

        // Update currentPackage properties reactively
        if (packageDefn) {
            Object.assign(this.currentPackage, packageDefn);
        }
        this.showBulkDefinitionsList = false;
        this.refreshPackageResults();
    },


    updateQueryDefn(defn) {
      Object.assign(this.currentPackage.params, defn);
      this.showTimelineQueryEditor = false;
    },

    setDateRange(startPeriod, endPeriod) {
      
      let me=this;
     
      me.queryDefn.startdate=startPeriod;
      me.queryDefn.enddate=endPeriod;
      //console.log("Set date range:"+defn.startdate +"=> "+defn.enddate);
     
    },
    sessionStateChanged(sessionState) {
      this.sessionState = sessionState;
    },
    
    selectGoal(goal) {
      this.currentPackage.params.goal = goal;
    },
    
    clearGoal() {
      this.currentPackage.params.goal = null;
      this.currentPackage.params.statsdefinition = '';
    },
    
    updateLocations(locations) {
      this.currentPackage.locations = locations;
    },
    
    updateStatsDefinition(definition) {
      this.currentPackage.params.statsdefinition = JSON.stringify(definition);
    },
    
    updateNotifications(configs) {
      this.currentPackage.notificationConfigs = configs;
      this.showNotificationEditor = false;
    },
    
    openAddressBook() {
      this.showAddressBook = true;
      this.$nextTick(() => {
        this.$refs.addressBookEditor?.refreshList();
      });
    },
    
    getNotificationTypeLabel(type) {
      const labels = {
        'JOB_COMPLETE_EMAIL': 'Job Complete',
        'JOB_ERROR_EMAIL': 'Job Error',
        'JOB_RESULTS_EMAIL': 'Job Results'
      };
      return labels[type] || type;
    },
    
    async loadPackage() {
      if (!this.vcCommunicator || !this.packageId) return;
      
      this.isLoading = true;
      try {
        const response = await this.vcCommunicator.loadMetadata(this.packageId);
        if (response) {
          this.metadata = {
            id: response.id,
            name: response.name,
            desc: response.desc,
            type: 'JobPackage'
          };
          this.currentPackage = response.content || this.currentPackage;
        }
      } catch (error) {
        console.error('Failed to load package:', error);
        window.VcCommonModals.showError('Load Error', 'Failed to load the job package.');
      } finally {
        this.isLoading = false;
      }
    },
    openResultFilterEditor() {
        this.showResultFilterEditor = true;
    },
    updateResultFilter(newFilter) {
        this.currentPackage.resultFilter = newFilter;
        this.showResultFilterEditor = false;
    },
    async savePackage() {
      if (!this.isValid || !this.vcCommunicator) return;
      
      this.isSaving = true;
      try {
        this.currentPackage.accountUserId=this.sessionState && this.sessionState.userMdId;
        const packageName = this.metadata.name || this.defaultName;
        const result = await this.vcCommunicator.saveBulkSourceDefn(
          this.metadata.id,
          packageName,
          this.metadata.desc,
          this.currentPackage
        );
        
        if (result && result.metadata) {
          this.metadata = result.metadata;
          this.$emit('saved', result.metadata);
          window.VcCommonModals.showToast('Package saved successfully');
        }
        this.delayedRefreshPackageResults();
      } catch (error) {
        console.error('Failed to save package:', error);
        window.VcCommonModals.showError('Save Error', 'Failed to save the job package.');
      } finally {
        this.isSaving = false;
      }
    },
    
    async saveAndRun() {
      await this.savePackage();
      if (this.metadata.id) {
        this.$emit('executed', this.metadata.id);
      }
    },
    delayedRefreshPackageResults(isError) {
     
      const delay = isError ||  this.pollingState.pollingRetries>10 ? 10000 : 5000;

      if (this.pollingState.pollingTimer) {
        clearTimeout(this.pollingState.pollingTimer);
      }

      this.pollingState.pollingTimer = setTimeout(() => {
        this.pollingState.pollingTimer = null;
        this.refreshPackageResults();
      }, delay);
    },

    refreshPreviewRawData() {
        var me=this;

        me.previewRawData.errorMessage=null;
        me.previewRawData.isRefreshing=true;
        me.previewRawData.resultData=null;
         
        var retrievalQueryDefn=JSON.parse(JSON.stringify(me.queryDefn));
        retrievalQueryDefn.options=VcListUtils.setValueInList(retrievalQueryDefn.options,"preview", true, false);
        let location = me.currentPackage.locations && me.currentPackage.locations.length > 0 ? me.currentPackage.locations[0]: null;
        if (!location) {
            me.previewRawData.errorMessage = "No location selected for preview.";
            me.previewRawData.isRefreshing = false;
            return;
        }
        vcCommunicator.retrieveTimelineData(retrievalQueryDefn, location.location, location.name, "json", true, false)
        .then(response => {
            me.previewRawData.resultData=response;
            me.previewRawData.isRefreshing=false;
        }).catch((error) => {
         
            me.previewRawData.errorMessage=error;
            me.previewRawData.isRefreshing=false;
        });
    },

    refreshPackageResults() {
        // Polling state grouped for clarity
        if (this.pollingState.hasExceedRetries) {
            console.error("Max polling retries reached");
            this.pollingState.isRefreshingResults = false;
            return;
        }
        this.pollingState.pollingRetries++;

        // Prevent concurrent polling
        if (this.pollingState.isPolling) {
            console.log("Already polling, skipping...");
            return;
        }

        console.log(`Polling for results...${this.metadata.id}, refreshing = ${this.pollingState.isRefreshingResults}`);

        if (!this.metadata || !this.metadata.id) {
            console.log("No metadata for refreshPackageResults");
            return;
        }

        this.pollingState.isPolling = true;

        vcCommunicator.resultsByJobPackage(this.metadata.id)
            .then((packageResults) => {
                if (packageResults && packageResults[0] && packageResults[0].instance) {
                    this.taskInstance = packageResults[0].instance;

                    if (packageResults[0].runonce) {
                    this.taskInstance.status = VcJobStatus.QUEUED;
                    }
                } else {
                    this.taskInstance = null;
                }

                // Continue refreshing only if not finished
                this.pollingState.isRefreshingResults =
                   (!this.taskInstance || !VcJobStatus.isFinishedState(this.taskInstance.status));

                if (this.pollingState.isRefreshingResults) {
                    this.delayedRefreshPackageResults();
                } else {
                    this.hasProcessed = true;
                    
                }
            })
            .catch((error) => {
              console.log("Failed resultsByJobPackage:", error);

              this.pollingState.isRefreshingResults =
                  this.pollingState.isRefreshingResults &&
                  (!this.taskInstance || !VcJobStatus.isFinishedState(this.taskInstance.status));

              if (this.pollingState.isRefreshingResults) {
                  this.delayedRefreshPackageResults(true);
              }
            })
            .finally(() => {
              this.pollingState.isPolling = false;
            });
    },
       
        getHour(time) {
            return parseInt(time.split(':')[0]) || 0;
        },
        getMinute(time) {
            return parseInt(time.split(':')[1]) || 0;
        },
        sortRunTimes() {
        this.currentPackage.runTimes.sort((a, b) => {
            const [ah, am] = a.split(':').map(Number);
            const [bh, bm] = b.split(':').map(Number);
            return ah !== bh ? ah - bh : am - bm;
        });
        },
        addRunTime() {
            if (!this.currentPackage.runTimes) this.currentPackage.runTimes = [];
            let newTime = '00:00';
            if (this.currentPackage.runTimes.length > 0) {
                // Find the latest scheduled time
                const sorted = [...this.currentPackage.runTimes].sort((a, b) => {
                const [ah, am] = a.split(':').map(Number);
                const [bh, bm] = b.split(':').map(Number);
                return ah !== bh ? ah - bh : am - bm;
                });
                const latest = sorted[sorted.length - 1];
                let [hour, minute] = latest.split(':').map(Number);
                let nextHour = hour + 1;
                if (nextHour >= 24) {
                // If next hour is next day, repeat the latest time
                nextHour = hour;
                }
                newTime = `${String(nextHour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
            }
            this.currentPackage.runTimes.push(newTime);
            this.sortRunTimes();
        },

        removeRunTime(idx) {
        this.currentPackage.runTimes.splice(idx, 1);
        this.sortRunTimes();
        },

        setRunTimeHour(idx, hour) {
        const minute = this.getMinute(this.currentPackage.runTimes[idx]);
        this.currentPackage.runTimes[idx] = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        this.sortRunTimes();
        },

        setRunTimeMinute(idx, minute) {
        const hour = this.getHour(this.currentPackage.runTimes[idx]);
        this.currentPackage.runTimes[idx] = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        this.sortRunTimes();
        },

        updateRunTimes(hours, minutes) {
        this.currentPackage.runTimes = hours.map((h, i) => {
            const m = minutes[i] ?? 0;
            return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        });
        this.sortRunTimes();
        },
    },
    watch: {

        queryDefinition: {
            handler(newVal) {
                this.refreshPreviewRawData();
            },
            deep: true
        }
    },
};
</script>

<style scoped>
.bulk-task-job-package-editor {
  max-width: 1200px;
  margin: 0 auto;
}

.goal-card {
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.goal-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.goal-card:active {
  transform: translateY(0);
}
</style>